import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Groups2Icon from '@mui/icons-material/Groups2';
import InfoIcon from '@mui/icons-material/Info';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import {
  Alert,
  AlertTitle,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';

import TableBodyLoader from '../../../../shared/components/TableBodyLoader/TableBodyLoader';
import reorderArrayItem from '../../../../utils/reorderArrayItem';
import Segment from '../../interfaces/Segment';
import RMFConfigTableRow from '../RFMConfigTableRow/RFMConfigTableRow';

interface RMFConfigTableProps {
  segments: Segment[];
  loading: boolean;
  onUpdateConfig: (segments: Segment[]) => Promise<void>;
}

const rmfConfigTableHeaders = [
  {
    label: 'Recency',
    description: 'How long ago they made a purchase',
  },
  {
    label: 'Frequency',
    description: 'How often they make purchases',
  },
  {
    label: 'Monetary',
    description: 'How much money they spend',
  },
];

const RMFConfigTable: FC<RMFConfigTableProps> = ({
  segments,
  loading,
  onUpdateConfig,
}) => {
  const handleUpdateConfig = (index: number, newConfig: Segment) => {
    const updatedConfig = [...segments];
    updatedConfig[index] = newConfig;
    return onUpdateConfig(updatedConfig);
  };

  const handleDeleteSegment = (deleteConfig: Segment) => {
    const updatedConfig = segments.filter((c) => c.id !== deleteConfig.id);
    return onUpdateConfig(updatedConfig);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const movedItems = reorderArrayItem(
      segments,
      result.source.index,
      result.destination.index
    );

    onUpdateConfig(movedItems);
  };

  if (!segments.length && !loading)
    return (
      <Alert severity='info'>
        <AlertTitle>No RFM Segments Found</AlertTitle>
        Click on <strong>Add Segment</strong> to add a Segment.
      </Alert>
    );

  return (
    <TableContainer component={Paper}>
      <Table>
        <caption>
          An RFM analysis evaluates clients and customers by scoring them in
          three categories
          <br /> How recently they’ve made a purchase, How often they buy, and
          The size of their purchases.
        </caption>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Name</TableCell>
            {rmfConfigTableHeaders.map(({ label, description }) => (
              <TableCell colSpan={2} key={label}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}
                >
                  {label}
                  <Tooltip title={description}>
                    <InfoIcon fontSize='small' />
                  </Tooltip>
                </Box>
              </TableCell>
            ))}
            <TableCell>Edit</TableCell>
            <TableCell>Delete</TableCell>
            <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
              <LowPriorityIcon fontSize='small' />
            </TableCell>
          </TableRow>
        </TableHead>
        {loading ? (
          <TableBodyLoader colSpan={11} rowsPerPage={7} />
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId='drop'>
              {(provided) => (
                <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                  <TableRow>
                    <TableCell>
                      <EmojiEventsIcon fontSize='small' color='primary' />
                    </TableCell>
                    <TableCell>
                      <Groups2Icon fontSize='small' color='primary' />
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        Min
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        Max
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        Min
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        Max
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        Min
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        Max
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        -
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        -
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color='text.secondary' variant='body2'>
                        -
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {segments.map((eachConfig, index) => (
                    <Draggable
                      key={eachConfig.id}
                      draggableId={'q-' + eachConfig.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <RMFConfigTableRow
                          segments={segments}
                          config={eachConfig}
                          priority={index + 1}
                          draggableProvided={provided}
                          draggableStateSnapshot={snapshot}
                          onUpdate={handleUpdateConfig.bind(null, index)}
                          onDelete={handleDeleteSegment}
                        />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Table>
    </TableContainer>
  );
};
export default RMFConfigTable;
